import { Component, Inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';

export interface FailedCompanyData {
  company_name: string;
  row_index: number;
  contract_policy_number: string;
  missing_fields: string[];
  suggested_values: { [key: string]: string };
}

export interface FailedCompaniesDialogData {
  failed_companies: FailedCompanyData[];
  processed_count: number;
  created_contracts: number;
  updated_contracts: number;
}

@Component({
  selector: 'app-failed-companies-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatCardModule,
    MatIconModule,
    MatDividerModule
],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>warning</mat-icon>
      Feltöltés eredménye
    </h2>
    
    <mat-dialog-content class="dialog-content">
      <!-- Sikeres feldolgozás összefoglalója -->
      <div class="success-summary">
        <h3>✅ Részleges siker - feldolgozott adatok:</h3>
        <div class="summary-stats">
          <div class="stat">
            <span class="label">Sikeresen feldolgozott sorok:</span>
            <span class="value">{{data.processed_count}}</span>
          </div>
          <div class="stat">
            <span class="label">Létrehozott szerződések:</span>
            <span class="value">{{data.created_contracts}}</span>
          </div>
          <div class="stat">
            <span class="label">Frissített szerződések:</span>
            <span class="value">{{data.updated_contracts}}</span>
          </div>
        </div>
      </div>
    
      <mat-divider></mat-divider>
    
      <!-- Hibás cégek listája -->
      @if (data.failed_companies.length > 0) {
        <div class="failed-companies">
          <h3>⚠️ Hibás cégek ({{data.failed_companies.length}} db):</h3>
          <p class="error-description">
            Az alábbi cégek sorai ki lettek hagyva a feltöltésből, mert hiányoznak a kötelező adatok (adószám vagy cégjegyzékszám).
            A többi sor sikeresen fel lett dolgozva. Ha szeretné, később manuálisan javíthatja ezeket az adatokat:
          </p>
          @for (company of data.failed_companies; track trackByCompany($index, company)) {
            <mat-card class="company-card">
              <mat-card-header>
                <mat-card-title>{{company.company_name}}</mat-card-title>
                <mat-card-subtitle>
                  {{company.row_index}}. sor - Szerződés: {{company.contract_policy_number}}
                </mat-card-subtitle>
              </mat-card-header>
              <mat-card-content>
                <div class="missing-fields">
                  <h4>Hiányzó mezők:</h4>
                  <div class="field-inputs">
                    @for (field of company.missing_fields; track field) {
                      <mat-form-field appearance="outline">
                        <mat-label>{{getFieldDisplayName(field)}}</mat-label>
                        <input
                          matInput
                          [value]="company.suggested_values[field] || ''"
                          (input)="updateFieldValue(company, field, $event)"
                          [placeholder]="getFieldPlaceholder(field)"
                          >
                        <mat-hint>{{getFieldHint(field)}}</mat-hint>
                      </mat-form-field>
                    }
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          }
        </div>
      }
    </mat-dialog-content>
    
    <mat-dialog-actions align="end" class="dialog-actions">
      <button mat-button (click)="onCancel()">
        Bezárás (hibás sorok kihagyása)
      </button>
      @if (data.failed_companies.length > 0) {
        <button
          mat-raised-button
          color="primary"
          (click)="onRetryWithData()"
          [disabled]="!hasValidData()"
          >
          Újrapróbálkozás javított adatokkal
        </button>
      }
    </mat-dialog-actions>
    `,
  styles: [`
    .dialog-content {
      min-width: 600px;
      max-width: 800px;
      max-height: 80vh;
      overflow-y: auto;
    }

    .success-summary {
      background: #e8f5e8;
      padding: 16px;
      border-radius: 8px;
      margin-bottom: 16px;
    }

    .summary-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 12px;
      margin-top: 12px;
    }

    .stat {
      display: flex;
      justify-content: space-between;
      padding: 8px 12px;
      background: white;
      border-radius: 4px;
      border-left: 4px solid #4caf50;
    }

    .label {
      font-weight: 500;
      color: #666;
    }

    .value {
      font-weight: bold;
      color: #4caf50;
    }

    .failed-companies {
      margin-top: 16px;
    }

    .error-description {
      color: #666;
      margin-bottom: 16px;
      line-height: 1.4;
    }

    .company-card {
      margin-bottom: 16px;
      border-left: 4px solid #ff9800;
    }

    .missing-fields h4 {
      margin: 0 0 12px 0;
      color: #333;
      font-size: 14px;
    }

    .field-inputs {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 16px;
    }

    .dialog-actions {
      padding: 16px 24px;
      gap: 12px;
    }

    mat-card-title {
      font-size: 16px !important;
      font-weight: 600 !important;
      color: #333 !important;
    }

    mat-card-subtitle {
      font-size: 12px !important;
      color: #666 !important;
    }

    h3 {
      margin: 0 0 12px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  `]
})
export class FailedCompaniesDialogComponent {
  
  constructor(
    public dialogRef: MatDialogRef<FailedCompaniesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FailedCompaniesDialogData
  ) {}

  trackByCompany(index: number, company: FailedCompanyData): string {
    return `${company.company_name}-${company.row_index}`;
  }

  getFieldDisplayName(field: string): string {
    switch (field) {
      case 'taxnumber': return 'Adószám';
      case 'companyregistrationnumber': return 'Cégjegyzékszám';
      default: return field;
    }
  }

  getFieldPlaceholder(field: string): string {
    switch (field) {
      case 'taxnumber': return 'pl. 12345678-1-23';
      case 'companyregistrationnumber': return 'pl. 01-09-123456';
      default: return '';
    }
  }

  getFieldHint(field: string): string {
    switch (field) {
      case 'taxnumber': return 'A cég adószáma (kötelező)';
      case 'companyregistrationnumber': return 'A cég cégjegyzékszáma (kötelező)';
      default: return '';
    }
  }

  updateFieldValue(company: FailedCompanyData, field: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!company.suggested_values) {
      company.suggested_values = {};
    }
    company.suggested_values[field] = target.value;
  }

  hasValidData(): boolean {
    return this.data.failed_companies.every(company => 
      company.missing_fields.every(field => 
        company.suggested_values[field] && 
        company.suggested_values[field].trim().length > 0
      )
    );
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onRetryWithData(): void {
    this.dialogRef.close({ 
      action: 'retry', 
      companies_data: this.data.failed_companies 
    });
  }
}